import gsap from 'gsap';
import { CanvasTexture, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D, SphereGeometry } from "three";
import { ScenesManager } from "../ScenesManager";
import { availableFeatures } from 'data/content.types';

export class Interior extends Object3D {
    material: MeshStandardMaterial;
    mesh!: Mesh;
    materialFeatures!: MeshBasicMaterial;
    meshFeatures!: Mesh;
    gradientCoords = {
        // [availableFeatures.cambio]: {x: 2999, y: 1958, width: 500, height: 500}, // cambio
        [availableFeatures.telas]: [{x: 2885, y: 1661, width: 450, height: 75}], // tela
        [availableFeatures.somBose]: [{x: 1580, y: 1499, width: 400, height: 600}, {x: 4480, y: 1499, width: 400, height: 600}], // som
    }
    constructor() {
        super();
        const geometry = new SphereGeometry( 2.8, 60, 40 );
        geometry.scale( -1, 1, 1 );

        this.material = new MeshStandardMaterial( {
            map: ScenesManager.assets.interior,
            alphaMap: ScenesManager.assets.interiorAlpha,
            transparent: true,
            opacity: 0,
            envMap: ScenesManager.assets.hdrInterior,
            envMapIntensity: 7,
        } );

        this.mesh = new Mesh( geometry, this.material );
        this.mesh.rotation.y = 1.536;
        this.mesh.position.set(0,0.436,0);
        this.mesh.scale.set(0,0,0);
        this.mesh.renderOrder = 2;
        this.add( this.mesh );


        // const allFeatures = ScenesManager.INTERNAL_FEATURES_DATA.somBose.active === true &&
        // ScenesManager.INTERNAL_FEATURES_DATA.telas.active === true;
        const allFeatures = ScenesManager.INTERNAL_REVEALEAD;
        if (!allFeatures) {
            const featuresTexture = new CanvasTexture(this.drawRadialGradients())
            this.materialFeatures = new MeshBasicMaterial( {
                color: 0x000000,
                alphaMap: featuresTexture,
                transparent: true,
                opacity: 0,
                depthTest: false,
                depthWrite: false
            } );
            this.meshFeatures = new Mesh(geometry, this.materialFeatures)
            this.meshFeatures.rotation.y = 1.536;
            this.meshFeatures.position.set(0, 0.436, 0);
            this.meshFeatures.scale.set(0, 0, 0);
            this.meshFeatures.renderOrder = 2;
            this.add(this.meshFeatures);
        }
    }

    show() {
        ScenesManager.directionalLight.visible = false;
        this.mesh.scale.set(1,1,1);
        gsap.to(this.material, {
            opacity: 1,
            delay: ScenesManager.INTERNAL_REVEALEAD ? 0 : 0.1,
            duration: ScenesManager.INTERNAL_REVEALEAD ? .6 : 1.4,
        })
        if (this.materialFeatures) {
            this.meshFeatures.scale.set(1,1,1);
            gsap.to(this.materialFeatures, {
                opacity: 0.999,
                duration: 0.5,
            })
        }
    }

    hide() {
        ScenesManager.directionalLight.visible = true;
        if (this.materialFeatures) {
            this.meshFeatures.scale.set(0,0,0);
            gsap.to(this.materialFeatures, {
                opacity: 0,
                duration: 0.4,
            })
        }
        gsap.to(this.mesh.scale, {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.3,
            onComplete: () => {
                this.mesh.scale.set(0,0,0);
            }
        })
        gsap.to(this.material, {
            opacity: 0,
            duration: 0.4,
        })
    }

    drawRadialGradients(options?: {
        overrideFeature?: string,
        customX?: number,
        customY?: number,
        customWidth?: number,
        customHeight?: number
    }) {
      const canvas = document.createElement('canvas');
      canvas.width = 6000;
      canvas.height = 3000;

      const ctx = canvas.getContext('2d');
      
      if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          for (let key in ScenesManager.INTERNAL_FEATURES_DATA) {
            if (ScenesManager.INTERNAL_FEATURES_DATA[key] && ScenesManager.INTERNAL_FEATURES_DATA[key].active) {
                const coordsArray = this.gradientCoords[key];
                if (!coordsArray || !coordsArray.length) continue;
                
                coordsArray.forEach(coord => {
                    const x = coord.x;
                    const y = coord.y;
                    const width = coord.width || 500;
                    const height = coord.height || 500;
                   
                    let overlappingGradient: string | null = null;
                    type CoordType = {x: number, y: number, width?: number, height?: number};
                    let overlappingCoord: CoordType | null = null;
                    
                    for (let otherKey in this.gradientCoords) {
                        if (otherKey !== key && ScenesManager.INTERNAL_FEATURES_DATA[otherKey]?.active) {
                            const otherCoordsArray = this.gradientCoords[otherKey];
                            if (!otherCoordsArray) continue;
                            
                            for (let otherCoord of otherCoordsArray) {
                                const otherX = otherCoord.x;
                                const otherY = otherCoord.y;
                                const distance = Math.sqrt(Math.pow(x - otherX, 2) + Math.pow(y - otherY, 2));
                                if (distance < Math.max(width, height)) {
                                    overlappingGradient = otherKey;
                                    overlappingCoord = otherCoord;
                                    break;
                                }
                            }
                            
                            if (overlappingGradient) break;
                        }
                    }

                    if (overlappingGradient && overlappingCoord) {
                        const otherX = overlappingCoord.x;
                        const otherY = overlappingCoord.y;
                        const otherWidth = overlappingCoord.width || 500;
                        const otherHeight = overlappingCoord.height || 500;
                        
                        const unifiedX = (x + otherX) / 2;
                        const unifiedY = (y + otherY) / 2;
                        const unifiedWidth = Math.max(width, otherWidth) * 1.5;
                        const unifiedHeight = Math.max(height, otherHeight) * 1.5;
                        
                        ctx.save();
                        ctx.translate(unifiedX, unifiedY);
                        
                        const maxUnifiedDimension = Math.max(unifiedWidth, unifiedHeight);
                        ctx.scale(unifiedWidth / maxUnifiedDimension, unifiedHeight / maxUnifiedDimension);
                        
                        const unifiedGradient = ctx.createRadialGradient(
                            0, 0, maxUnifiedDimension * 0.3,
                            0, 0, maxUnifiedDimension
                        );
                        unifiedGradient.addColorStop(0, '#161616');
                        unifiedGradient.addColorStop(1, 'white');

                        ctx.fillStyle = unifiedGradient;
                        ctx.beginPath();
                        ctx.arc(0, 0, maxUnifiedDimension, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.restore();
                    } else {
                        ctx.save();
                        ctx.translate(x, y);
                        
                        const maxDimension = Math.max(width, height);
                        ctx.scale(width / maxDimension, height / maxDimension);
                        
                        const gradient = ctx.createRadialGradient(
                            0, 0, maxDimension * 0.3,
                            0, 0, maxDimension
                        );
                        gradient.addColorStop(0, '#161616');
                        gradient.addColorStop(1, 'white');
                    
                        ctx.fillStyle = gradient;
                        ctx.beginPath();
                        ctx.arc(0, 0, maxDimension, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.restore();
                    }
                });
            }
        }
      }
    
      return canvas;
    }
}